import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Flag, ShieldAlert, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportDialog({ userId, userName, onClose, onBlocked }) {
  const { token } = useAuth();
  const [mode, setMode] = useState('choose'); // 'choose', 'report', 'done'
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);

  const handleBlock = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/block`, { user_id: userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onBlocked?.();
      setMode('done');
    } catch (e) {
      console.error('Block failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    if (!reason) return;
    setLoading(true);
    try {
      await axios.post(`${API}/report`, {
        user_id: userId,
        reason,
        details: details || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onBlocked?.();
      setMode('done');
    } catch (e) {
      console.error('Report failed:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-[#111114] rounded-2xl border border-zinc-800 p-6">
        {/* Close */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit font-semibold text-white">
            {mode === 'choose' && `Options for ${userName}`}
            {mode === 'report' && 'Report User'}
            {mode === 'done' && 'Done'}
          </h3>
          <button
            data-testid="close-report-dialog"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {mode === 'choose' && (
          <div className="space-y-3">
            <Button
              data-testid="block-user-btn"
              onClick={handleBlock}
              disabled={loading}
              variant="outline"
              className="w-full justify-start gap-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-12"
            >
              <ShieldAlert className="w-5 h-5 text-rose-500" />
              {loading ? 'Blocking...' : 'Block User'}
            </Button>
            <Button
              data-testid="report-user-btn"
              onClick={() => setMode('report')}
              variant="outline"
              className="w-full justify-start gap-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-12"
            >
              <Flag className="w-5 h-5 text-orange-500" />
              Report User
            </Button>
          </div>
        )}

        {mode === 'report' && (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="report-reason-select" className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="spam">Spam</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                  <SelectItem value="fake">Fake profile</SelectItem>
                  <SelectItem value="harassment">Harassment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Details (optional)</Label>
              <Textarea
                data-testid="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Tell us more..."
                rows={3}
                className="bg-zinc-900 border-zinc-800 text-white resize-none"
              />
            </div>
            <Button
              data-testid="submit-report-btn"
              onClick={handleReport}
              disabled={!reason || loading}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white rounded-full"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Report
            </Button>
          </div>
        )}

        {mode === 'done' && (
          <div className="text-center py-4">
            <ShieldAlert className="w-12 h-12 text-rose-500 mx-auto mb-3" />
            <p className="text-zinc-300 mb-4">User has been blocked and reported. You won't see them again.</p>
            <Button
              data-testid="done-btn"
              onClick={onClose}
              className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-full"
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
